import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { 
  Package, ShoppingCart, ArrowDownCircle, Users, Building2, 
  TrendingUp, UserCog, Shield, DollarSign, Store, Gift,
  CheckCircle, BarChart3, CreditCard, Receipt, Calendar,
  Smartphone, Zap, Lock, Cloud, Settings, ArrowRight, MessageSquare,
  Loader2, Sparkles, Search
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

const slideInFromLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
};

const slideInFromRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
};

import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

export default function Landing() {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const yBackground = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const yHero = useTransform(scrollYProgress, [0, 0.3], [0, 100]);
  
  const [signupLoading, setSignupLoading] = useState(false);
  const [signinLoading, setSigninLoading] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [userName, setUserName] = useState<string | null>(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [apiCaixa, setApiCaixa] = useState<CarouselApi | null>(null);
  const [apiCadastros, setApiCadastros] = useState<CarouselApi | null>(null);
  const [apiModais, setApiModais] = useState<CarouselApi | null>(null);
  const [apiConfigs, setApiConfigs] = useState<CarouselApi | null>(null);
  const [apiRaiox, setApiRaiox] = useState<CarouselApi | null>(null);

  // Buscar planos do banco de dados
  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ["subscription-plans-landing"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("status", "active")
        .order("price", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });
  
  const texts = useMemo(() => [
    "Gerencie seu caixa",
    "Registre entradas",
    "Registre saídas",
    "Consulte relatórios"
  ], []);
  
  useEffect(() => {
    let currentTextIndex = 0;
    let index = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const typeForward = () => {
      const currentText = texts[currentTextIndex];
      if (index <= currentText.length) {
        setTypedText(currentText.slice(0, index));
        index += 1;
        timeoutId = setTimeout(typeForward, 100);
      } else {
        // Texto completo, espera alguns segundos antes de apagar
        timeoutId = setTimeout(() => {
          // Apaga o texto
          const eraseText = () => {
            if (index > 0) {
              index -= 1;
              setTypedText(currentText.slice(0, index));
              timeoutId = setTimeout(eraseText, 50);
            } else {
              // Texto apagado, espera um pouco e vai para o próximo
              currentTextIndex = (currentTextIndex + 1) % texts.length;
              index = 0;
              timeoutId = setTimeout(typeForward, 500);
            }
          };
          eraseText();
        }, 2000); // Espera 2 segundos com o texto completo antes de apagar
      }
    };

    typeForward();

    return () => clearTimeout(timeoutId);
  }, [texts]);

  useEffect(() => {
    if (!apiCaixa) return;
    const id = setInterval(() => apiCaixa.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [apiCaixa]);
  useEffect(() => {
    if (!apiCadastros) return;
    const id = setInterval(() => apiCadastros.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [apiCadastros]);
  useEffect(() => {
    if (!apiModais) return;
    const id = setInterval(() => apiModais.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [apiModais]);
  useEffect(() => {
    if (!apiConfigs) return;
    const id = setInterval(() => apiConfigs.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [apiConfigs]);
  useEffect(() => {
    if (!apiRaiox) return;
    const id = setInterval(() => apiRaiox.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [apiRaiox]);

  useEffect(() => {
    let isMounted = true;

    const processSession = async (session: any) => {
      if (!isMounted) return;
      setAuthChecking(true);

      if (!session?.user) {
        setUserName(null);
        setAuthChecking(false);
        return;
      }

      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("nome_completo")
          .eq("id", session.user.id)
          .maybeSingle();

        const name =
          profile?.nome_completo ||
          session.user.user_metadata?.full_name ||
          session.user.email?.split("@")[0] ||
          null;

        if (!isMounted) return;
        setUserName(name);
      } catch (error) {
        console.error("Erro ao buscar perfil do usuário:", error);
        if (isMounted) {
          setUserName(session.user.user_metadata?.full_name || session.user.email || null);
        }
      } finally {
        if (isMounted) {
          setAuthChecking(false);
        }
      }
    };

    const loadSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await processSession(session);
      } catch (error) {
        console.error("Erro ao carregar sessão do usuário:", error);
        if (isMounted) {
          setUserName(null);
          setAuthChecking(false);
        }
      }
    };

    loadSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      processSession(session);
    });

    return () => {
      isMounted = false;
      authListener.subscription?.unsubscribe();
    };
  }, []);

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSignupLoading(true);
    const formData = new FormData(e.currentTarget);
    const email = formData.get("signup-email") as string;
    const password = formData.get("signup-password") as string;
    const nome = formData.get("signup-nome") as string;
    const empresa = formData.get("signup-empresa") as string;

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            nome_completo: nome,
            company_name: empresa,
            company_slug: empresa.toLowerCase().replace(/[^a-z0-9]/g, '-')
          }
        }
      });
      if (error) throw error;
      toast.success("Conta criada! Redirecionando...");
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (error: any) {
      toast.error(error.message || "Erro ao criar conta");
    } finally {
      setSignupLoading(false);
    }
  };

  const handleSignin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSigninLoading(true);
    const formData = new FormData(e.currentTarget);
    const email = formData.get("signin-email") as string;
    const password = formData.get("signin-password") as string;

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      toast.success("Login realizado! Redirecionando...");
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Erro ao fazer login");
    } finally {
      setSigninLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setSigninLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (error) throw error;
    } catch (error: any) {
      toast.error("Erro ao conectar com Google: " + (error?.message || ""));
    } finally {
      setSigninLoading(false);
    }
  };

  const displayName = userName ? (userName.split(" ").filter(Boolean)[0] || userName) : null;

  return (
    <div className="dark min-h-screen bg-slate-950 text-white overflow-x-hidden">
      <PublicHeader />
      {/* Hero Section with Form */}
      <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden pt-16">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/fundo-landing-page-1.jpg)' }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/70 to-secondary/80" />
        
        <motion.div 
          className="absolute inset-0 opacity-10"
          style={{ y: yBackground }}
        >
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute top-60 right-20 w-72 h-72 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full bg-white/20 blur-3xl animate-pulse" />
        </motion.div>

        <div className="container mx-auto px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-[1fr,400px] gap-16 items-center justify-items-end">
            {/* Left: Hero Text */}
            <motion.div
              className="text-white space-y-8 pr-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              style={{ y: yHero }}
            >
              <div className="flex justify-start mb-4">
                <img 
                  src="/logo-fundo-branco.png" 
                  alt="Caixa Fácil Já" 
                  className="h-[90px] md:h-[108px] w-auto"
                />
              </div>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight">
                {typedText}
                <motion.span
                  className="inline-block w-1 h-10 md:h-14 bg-white ml-2"
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              </h1>
              <p className="text-xl md:text-2xl opacity-90 max-w-xl">
                Registre vendas e despesas com <strong>facilidade</strong>. <br />
                Veja entradas, saídas e saldo <strong>somado e pronto</strong>. <br />
                Relatórios por <strong>conta, pix, crédito e débido</strong>.
              </p>
              <div className="flex flex-wrap gap-4 text-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  <span>Teste grátis 14 dias</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  <span>Sem cartão</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6" />
                  <span>Cancele quando quiser</span>
                </div>
              </div>
            </motion.div>

            {/* Right: Auth Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-full max-w-sm pl-8"
            >
              {authChecking ? (
                <Card className="shadow-2xl border border-slate-800 bg-slate-900 rounded-2xl overflow-hidden w-full">
                  <CardContent className="px-6 py-12 flex flex-col items-center justify-center gap-3">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
                    <span className="text-sm text-slate-400">Carregando...</span>
                  </CardContent>
                </Card>
              ) : displayName ? (
                <Card className="shadow-2xl border border-slate-800 bg-slate-900 rounded-2xl overflow-hidden w-full">
                  <CardHeader className="space-y-3 pb-6 pt-8">
                    <CardTitle className="text-3xl font-bold text-center text-slate-100">
                      Olá, {displayName}
                    </CardTitle>
                    <CardDescription className="text-center text-base text-slate-400">
                      Que bom te ver por aqui novamente.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-6 pb-8 flex flex-col gap-4">
                    <Button
                      className="w-full h-10 text-sm font-semibold bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                      onClick={() => navigate("/dashboard")}
                    >
                      Entrar no Caixa Fácil
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <p className="text-center text-sm text-slate-500">
                      Continue gerenciando o seu caixa com praticidade.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="shadow-2xl border border-slate-800 bg-slate-900 rounded-2xl overflow-hidden w-full">
                  <CardHeader className="space-y-2 pb-4 pt-6">
                    <CardTitle className="text-2xl font-bold text-center text-slate-100">
                      Comece Agora
                    </CardTitle>
                    <CardDescription className="text-center text-sm text-slate-400">
                      Crie sua conta gratuitamente ou faça login
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    <Tabs defaultValue="signin" className="w-full">
                      <TabsList className="grid w-full grid-cols-2 mb-4 rounded-lg bg-slate-800/70 p-0.5 h-10">
                        <TabsTrigger 
                          value="signin" 
                          className="text-xs font-semibold text-slate-200 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all duration-200"
                        >
                          Entrar
                        </TabsTrigger>
                        <TabsTrigger 
                          value="signup" 
                          className="text-xs font-semibold text-slate-200 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all duration-200"
                        >
                          Criar Conta
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="signin" className="space-y-0">
                        <form onSubmit={handleSignin} className="space-y-3">
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <Label htmlFor="signin-email" className="text-xs font-medium text-slate-200">Email</Label>
                              <Input
                                id="signin-email"
                                name="signin-email"
                                type="email"
                                required
                                placeholder="seu@email.com"
                                className="h-9 bg-slate-900 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded-md transition-colors text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="signin-password" className="text-xs font-medium text-slate-200">Senha</Label>
                              <Input
                                id="signin-password"
                                name="signin-password"
                                type="password"
                                required
                                placeholder="••••••••"
                                className="h-9 bg-slate-900 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded-md transition-colors text-sm"
                              />
                            </div>
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full h-9 text-sm font-semibold bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors" 
                            disabled={signinLoading}
                          >
                            {signinLoading ? "Entrando..." : "Entrar na Conta"}
                          </Button>
                          <div className="relative my-3">
                            <div className="absolute inset-0 flex items-center">
                              <Separator className="border-slate-700" />
                            </div>
                            <div className="relative flex justify-center text-xs">
                              <span className="bg-slate-900 px-2 text-slate-400">Ou continue com</span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full h-9 text-sm font-medium border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-100 rounded-md transition-colors"
                            onClick={handleGoogleSignIn}
                            disabled={signinLoading}
                          >
                            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            Continuar com Google
                          </Button>
                        </form>
                      </TabsContent>

                      <TabsContent value="signup">
                        <form onSubmit={handleSignup} className="space-y-3">
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <Label htmlFor="signup-nome" className="text-xs font-medium text-slate-200">Nome Completo</Label>
                              <Input
                                id="signup-nome"
                                name="signup-nome"
                                required
                                placeholder="João Silva"
                                className="h-9 bg-slate-900 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded-md transition-colors text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="signup-empresa" className="text-xs font-medium text-slate-200">Nome da Empresa</Label>
                              <Input
                                id="signup-empresa"
                                name="signup-empresa"
                                required
                                placeholder="Minha Loja"
                                className="h-9 bg-slate-900 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded-md transition-colors text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="signup-email" className="text-xs font-medium text-slate-200">Email</Label>
                              <Input
                                id="signup-email"
                                name="signup-email"
                                type="email"
                                required
                                placeholder="seu@email.com"
                                className="h-9 bg-slate-900 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded-md transition-colors text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor="signup-password" className="text-xs font-medium text-slate-200">Senha</Label>
                              <Input
                                id="signup-password"
                                name="signup-password"
                                type="password"
                                required
                                placeholder="••••••••"
                                className="h-9 bg-slate-900 border border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-500/50 rounded-md transition-colors text-sm"
                              />
                            </div>
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full h-9 text-sm font-semibold bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors" 
                            disabled={signupLoading}
                          >
                            {signupLoading ? "Criando conta..." : "Começar Grátis por 14 Dias"}
                          </Button>
                          <div className="relative my-3">
                            <div className="absolute inset-0 flex items-center">
                              <Separator className="border-slate-700" />
                            </div>
                            <div className="relative flex justify-center text-xs">
                              <span className="bg-slate-900 px-2 text-slate-400">Ou cadastre-se com</span>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full h-9 text-sm font-medium border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-100 rounded-md transition-colors"
                            onClick={handleGoogleSignIn}
                            disabled={signupLoading}
                          >
                            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            Continuar com Google
                          </Button>
                        </form>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section id="recursos" className="py-8 bg-gradient-to-r from-primary to-secondary">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-white">
            {[
              { icon: Zap, text: "Interface Rápida e Intuitiva" },
              { icon: Lock, text: "Dados Seguros na Nuvem" },
              { icon: Smartphone, text: "Acesse de Qualquer Lugar" },
              { icon: Cloud, text: "Sem Instalação" }
            ].map((item, i) => (
              <motion.div 
                key={i}
                className="flex items-center gap-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <item.icon className="w-6 h-6" />
                <span className="font-medium">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="painel" className="py-24 bg-gradient-to-b from-background to-accent/20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-4">Visão Geral</h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">Acompanhe vendas, despesas e desempenho em tempo real. Decida com segurança.</p>
          </motion.div>
          <div className="grid lg:grid-cols-[2fr,1fr] gap-12 items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20" />
              <div className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full h-[580px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-visao-geral.png)', backgroundSize: '100% auto' }} />
            </div>
            <div>
              <h3 className="text-4xl md:text-5xl font-bold mb-4">Tudo em um só lugar</h3>
              <p className="text-lg md:text-xl text-muted-foreground mb-6">Acompanhe vendas, despesas e desempenho em tempo real. Decida com segurança, com um painel que mostra o que importa e permite agir rápido.</p>
              <div className="space-y-3 text-base">
                {[
                  { icon: TrendingUp, text: "Indicadores em tempo real para visão clara do negócio." },
                  { icon: Zap, text: "Alertas e status para agir no momento certo." },
                  { icon: CheckCircle, text: "Ações rápidas e atalhos para produtividade." },
                  { icon: BarChart3, text: "Decisões com dados, sem achismos." },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <b.icon className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{b.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="gestao-caixa" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-4">Caixa e Registros</h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">Clareza total de entradas e saídas, histórico detalhado e relatórios objetivos.</p>
          </motion.div>
          <Carousel opts={{ loop: true }} setApi={setApiCaixa}>
            <CarouselContent>
              <CarouselItem className="basis-full">
                <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                  <div className="rounded-2xl shadow-2xl border-4 border-secondary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-caixa.png)', backgroundSize: '100% auto' }} />
                  <div>
                    <h3 className="text-4xl md:text-5xl font-bold mb-4">O Raio-X Financeiro</h3>
                    <p className="text-lg md:text-xl text-muted-foreground mb-6">Visão consolidada por método de pagamento, status imediato e extrato detalhado para decisões rápidas e seguras.</p>
                    <div className="space-y-3 text-sm md:text-base">
                      {[
                        { icon: CreditCard, text: "Totais por Pix, débito, crédito e dinheiro." },
                        { icon: CheckCircle, text: "Status pago, pendente e confirmado." },
                        { icon: Receipt, text: "Histórico com cliente, fornecedor, categoria e valor." },
                        { icon: DollarSign, text: "Comparação de saldo atual e previsto." },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <b.icon className="w-5 h-5 text-secondary flex-shrink-0" />
                          <span>{b.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem className="basis-full">
                <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                  <div className="rounded-2xl shadow-2xl border-4 border-secondary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-registro-de-vendas.png)', backgroundSize: '100% auto' }} />
                  <div>
                    <h3 className="text-4xl md:text-5xl font-bold mb-4">Vendas com clareza</h3>
                    <p className="text-lg md:text-xl text-muted-foreground mb-6">Histórico filtrável por período, cliente e método de pagamento com exportação simples.</p>
                    <div className="space-y-3 text-sm md:text-base">
                      {[
                        { icon: Calendar, text: "Busca por período e cliente." },
                        { icon: Receipt, text: "Detalhes de itens e valores." },
                        { icon: CreditCard, text: "Métodos de pagamento registrados." },
                        { icon: ArrowRight, text: "Exportação com um clique." },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <b.icon className="w-5 h-5 text-secondary flex-shrink-0" />
                          <span>{b.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem className="basis-full">
                <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                  <div className="rounded-2xl shadow-2xl border-4 border-secondary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-relatorios.png)', backgroundSize: '100% auto' }} />
                  <div>
                    <h3 className="text-4xl md:text-5xl font-bold mb-4">Relatórios objetivos</h3>
                    <p className="text-lg md:text-xl text-muted-foreground mb-6">Análises de vendas e despesas com categorias, metas e compartilhamento.</p>
                    <div className="space-y-3 text-sm md:text-base">
                      {[
                        { icon: BarChart3, text: "Indicadores de desempenho por categoria." },
                        { icon: TrendingUp, text: "Acompanhamento de metas e tendências." },
                        { icon: CheckCircle, text: "Comparativos claros e práticos." },
                        { icon: ArrowRight, text: "Exportar e compartilhar com a equipe." },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <b.icon className="w-5 h-5 text-secondary flex-shrink-0" />
                          <span>{b.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      <section id="gestao-negocio" className="py-24 bg-gradient-to-b from-accent/20 to-background">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2 className="text-5xl md:text-6xl font-bold mb-4">Cadastros Principais</h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">Catálogo, clientes, fornecedores e usuários com velocidade.</p>
          </motion.div>
          <Carousel opts={{ loop: true }} setApi={setApiCadastros}>
            <CarouselContent>
              <CarouselItem className="basis-full">
                <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                  <div className="rounded-2xl shadow-2xl border-4 border-primary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-produtos.png)', backgroundSize: '100% auto' }} />
                  <div>
                    <h3 className="text-4xl md:text-5xl font-bold mb-4">Produtos</h3>
                    <p className="text-lg md:text-xl text-muted-foreground mb-6">Catálogo organizado com busca, filtros, preços e estoque atualizados.</p>
                    <div className="space-y-3 text-sm md:text-base">
                      {[
                        { icon: Package, text: "Busca e filtros por categoria." },
                        { icon: DollarSign, text: "Preços e estoque visíveis." },
                        { icon: ArrowRight, text: "Edição ágil em poucos cliques." },
                        { icon: CheckCircle, text: "Catálogo pronto para vender mais." },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <b.icon className="w-5 h-5 text-primary flex-shrink-0" />
                          <span>{b.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem className="basis-full">
                <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                  <div className="rounded-2xl shadow-2xl border-4 border-primary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-clientes.png)', backgroundSize: '100% auto' }} />
                  <div>
                    <h3 className="text-4xl md:text-5xl font-bold mb-4">Clientes</h3>
                    <p className="text-lg md:text-xl text-muted-foreground mb-6">Cadastro completo com histórico para atendimento contextual e fidelização.</p>
                    <div className="space-y-3 text-sm md:text-base">
                      {[
                        { icon: Users, text: "Dados, contatos e observações úteis." },
                        { icon: Receipt, text: "Histórico de compras por cliente." },
                        { icon: Search, text: "Filtros e buscas rápidos." },
                        { icon: CheckCircle, text: "Ações para fidelizar e vender mais." },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <b.icon className="w-5 h-5 text-primary flex-shrink-0" />
                          <span>{b.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem className="basis-full">
                <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                  <div className="rounded-2xl shadow-2xl border-4 border-primary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-fornecedores.png)', backgroundSize: '100% auto' }} />
                  <div>
                    <h3 className="text-4xl md:text-5xl font-bold mb-4">Fornecedores</h3>
                    <p className="text-lg md:text-xl text-muted-foreground mb-6">Histórico de compras e comparativos para negociar melhor e repor com confiança.</p>
                    <div className="space-y-3 text-sm md:text-base">
                      {[
                        { icon: Building2, text: "Cadastro claro e rápido." },
                        { icon: Receipt, text: "Histórico por fornecedor." },
                        { icon: DollarSign, text: "Comparar preços e condições." },
                        { icon: ArrowRight, text: "Facilitar reabastecimentos." },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <b.icon className="w-5 h-5 text-primary flex-shrink-0" />
                          <span>{b.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
              <CarouselItem className="basis-full">
                <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                  <div className="rounded-2xl shadow-2xl border-4 border-primary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-usuarios.png)', backgroundSize: '100% auto' }} />
                  <div>
                    <h3 className="text-4xl md:text-5xl font-bold mb-4">Usuários</h3>
                    <p className="text-lg md:text-xl text-muted-foreground mb-6">Permissões por função, segurança e colaboração para operação organizada.</p>
                    <div className="space-y-3 text-sm md:text-base">
                      {[
                        { icon: UserCog, text: "Perfis com acesso definido." },
                        { icon: Users, text: "Gestão de usuários simples." },
                        { icon: Shield, text: "Segurança e registro de ações." },
                        { icon: CheckCircle, text: "Produtividade em equipe." },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <b.icon className="w-5 h-5 text-primary flex-shrink-0" />
                          <span>{b.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      <section id="tudo-que-faz" className="py-24 bg-gradient-to-br from-secondary/10 via-primary/5 to-secondary/10">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2 className="text-5xl md:text-6xl font-bold mb-4">Modais de Cadastros</h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">Fluxos rápidos para cadastrar e editar sem fricção.</p>
          </motion.div>
          <Carousel opts={{ loop: true }} setApi={setApiModais}>
            <CarouselContent>
              {[
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-produto.png", title: "Produtos", desc: "Nome, preço, categoria e estoque com campos claros." },
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-cliente.png", title: "Clientes", desc: "Dados essenciais, observações e histórico." },
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-fornecedor.png", title: "Fornecedores", desc: "Contato, condições e histórico visível." },
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-usuario.png", title: "Usuários", desc: "Perfis e acesso definidos por função." },
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-despesa.png", title: "Despesas", desc: "Categoria, fornecedor, datas e parcelas." },
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-categoria-de-produto.png", title: "Categorias de Produto", desc: "Estruture o catálogo e agilize vendas." },
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-categoria-de-despesa.png", title: "Categorias de Despesa", desc: "Organize custos com validação simples." },
                { src: "/imagens/prints/caixa-facil-ja-tela-modal-vendas.png", title: "Vendas", desc: "Seleção de itens, pagamento e registro instantâneo." }
              ].map((item, i) => (
                <CarouselItem key={i} className="basis-full">
                  <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                    <div className="rounded-2xl shadow-2xl border-4 border-primary/20 w-full h-[640px] bg-center bg-no-repeat" style={{ backgroundImage: `url(${item.src})`, backgroundSize: item.title === 'Vendas' ? '70% auto' : 'cover' }} />
                    <div>
                      <h3 className="text-4xl md:text-5xl font-bold mb-4">{item.title}</h3>
                      <p className="text-lg md:text-xl text-muted-foreground mb-6">{item.desc}</p>
                      <div className="space-y-3 text-sm md:text-base">
                        {[
                          { icon: CheckCircle, text: "Campos claros e validações simples." },
                          { icon: ArrowRight, text: "Edição rápida quando necessário." },
                          { icon: Zap, text: "Cadastro sem fricção para produtividade." },
                          { icon: Calendar, text: "Organização para fechamento do mês." },
                        ].map((b, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <b.icon className="w-5 h-5 text-primary flex-shrink-0" />
                            <span>{b.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      <section id="controles-avancados" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2 className="text-5xl md:text-6xl font-bold mb-4">Configurações</h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">Contas, dados da empresa, perfil e categorias com controle total.</p>
          </motion.div>
          <Carousel opts={{ loop: true }} setApi={setApiConfigs}>
            <CarouselContent>
              {[
                { src: "/imagens/prints/caixa-facil-ja-tela-contas-bancarias.png", title: "Contas Bancárias", desc: "Saldos e movimentações consolidadas." },
                { src: "/imagens/prints/caixa-facil-ja-tela-dados-da-empresa.png", title: "Dados da Empresa", desc: "Branding, regras e comunicação profissional." },
                { src: "/imagens/prints/caixa-facil-ja-tela-meu-perfil.png", title: "Meu Perfil", desc: "Preferências, segurança e notificações." },
                { src: "/imagens/prints/caixa-facil-ja-tela-categorias-de-produtos.png", title: "Categorias de Produto", desc: "Agrupe e encontre itens em segundos." },
                { src: "/imagens/prints/caixa-facil-ja-tela-categorias-de-despesas.png", title: "Categorias de Despesa", desc: "Clareza de custos por tipo e relatórios organizados." }
              ].map((item, i) => (
                <CarouselItem key={i} className="basis-full">
                  <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                    <div className="rounded-2xl shadow-2xl border-4 border-secondary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: `url(${item.src})`, backgroundSize: '100% auto' }} />
                    <div>
                      <h3 className="text-4xl md:text-5xl font-bold mb-4">{item.title}</h3>
                      <p className="text-lg md:text-xl text-muted-foreground mb-6">{item.desc}</p>
                      <div className="space-y-3 text-sm md:text-base">
                        {[
                          { icon: Settings, text: "Configurações centralizadas e simples." },
                          { icon: BarChart3, text: "Visão consolidada para controle." },
                          { icon: Sparkles, text: "Preferências e branding ajustáveis." },
                          { icon: Receipt, text: "Organização para relatórios." },
                        ].map((b, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <b.icon className="w-5 h-5 text-secondary flex-shrink-0" />
                            <span>{b.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      <section id="raio-x" className="py-24 bg-gradient-to-b from-accent/20 to-background">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2 className="text-5xl md:text-6xl font-bold mb-4">Raio-X</h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">Relatórios focados em categorias, clientes, produtos, fornecedores e equipe.</p>
          </motion.div>
          <Carousel opts={{ loop: true }} setApi={setApiRaiox}>
            <CarouselContent>
              {[
                { src: "/imagens/prints/caixa-facil-ja-tela-raio-x-categoria-de-produto.png", title: "Categorias que vendem mais", desc: "Performance por categoria e comparações simples." },
                { src: "/imagens/prints/caixa-facil-ja-tela-raio-x-cliente.png", title: "Clientes que fazem diferença", desc: "Ranking, frequência e ticket médio." },
                { src: "/imagens/prints/caixa-facil-ja-tela-raio-x-fornecedor.png", title: "Raio-X de compras", desc: "Custos, prazos e oportunidades." },
                { src: "/imagens/prints/caixa-facil-ja-tela-raio-x-produto.png", title: "Produtos que mais rendem", desc: "Receita, demanda e rentabilidade." },
                { src: "/imagens/prints/caixa-facil-ja-tela-raio-x-usuario.png", title: "Desempenho da equipe", desc: "Vendas, conversão, metas e reconhecimento." }
              ].map((item, i) => (
                <CarouselItem key={i} className="basis-full">
                  <div className="grid lg:grid-cols-[2fr,1fr] gap-8 items-center">
                    <div className="rounded-2xl shadow-2xl border-4 border-primary/20 w-full h-[520px] bg-top bg-no-repeat" style={{ backgroundImage: `url(${item.src})`, backgroundSize: '100% auto' }} />
                    <div>
                      <h3 className="text-4xl md:text-5xl font-bold mb-4">{item.title}</h3>
                      <p className="text-lg md:text-xl text-muted-foreground mb-6">{item.desc}</p>
                      <div className="space-y-3 text-sm md:text-base">
                        {[
                          { icon: BarChart3, text: "Métricas e comparativos práticos." },
                          { icon: TrendingUp, text: "Tendências e insights acionáveis." },
                          { icon: DollarSign, text: "Foco em resultados e rentabilidade." },
                          { icon: CheckCircle, text: "Decisões baseadas em dados." },
                        ].map((b, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <b.icon className="w-5 h-5 text-primary flex-shrink-0" />
                            <span>{b.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </section>

      <section id="chatwoot" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2 className="text-5xl md:text-6xl font-bold mb-4">Atendimento no WhatsApp</h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">Integração com Chatwoot focada em WhatsApp para conversas que convertem.</p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary rounded-2xl blur-2xl opacity-20" />
                <div className="relative rounded-2xl shadow-2xl border-4 border-secondary/20 w-full h-[520px] bg-top bg-no-repeat bg-contain" style={{ backgroundImage: 'url(/imagens/prints/caixa-facil-ja-tela-chatwoot.png)' }} />
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-3xl font-bold">Vantagens para atendimento</h3>
              <ul className="space-y-3 text-lg">
                <li>Roteamento e atribuição por atendente para organizar filas e propriedade das conversas.</li>
                <li>Respostas rápidas e automações para reduzir tempo de resposta e padronizar atendimento.</li>
                <li>Notas internas e tags para contexto, classificação e colaboração entre a equipe.</li>
                <li>Histórico de contato e status de conversas para continuar atendimentos com contexto.</li>
                <li>Métricas de produtividade da equipe e acompanhamento de qualidade.</li>
              </ul>
              <div className="mt-6">
                <Button size="lg" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Começar no WhatsApp</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Caixa Section */}
      <section id="gestao-caixa" className="py-32 bg-background relative">
        <motion.div 
          className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Gestão de Caixa Completa
            </h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              Abra e feche o caixa, registre vendas e despesas com <strong>poucos cliques</strong>.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInFromLeft}
              className="space-y-6"
            >
              <h3 className="text-3xl md:text-4xl font-bold">Controle Diário Simplificado</h3>
              <p className="text-lg text-muted-foreground">
                Registre todas as movimentações do seu caixa em segundos. Interface intuitiva que qualquer pessoa consegue usar.
              </p>
              <div className="space-y-4">
                {[
                  { icon: ShoppingCart, text: "Vendas rápidas com múltiplas formas de pagamento" },
                  { icon: Receipt, text: "Despesas categorizadas por tipo" },
                  { icon: BarChart3, text: "Extratos detalhados e filtros avançados" },
                  { icon: Calendar, text: "Histórico completo de transações" }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-4 text-lg"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <span>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInFromRight}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-do-caixa-extratos-vendas-e-despesas.png" 
                  alt="Tela de caixa com vendas e despesas" 
                  className="relative rounded-2xl shadow-2xl border-4 border-secondary/20 w-full"
                />
              </div>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInFromLeft}
              whileHover={{ scale: 1.02 }}
              className="lg:order-2"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-cadastro-de-vendas.png" 
                  alt="Cadastro de vendas" 
                  className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full"
                />
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInFromRight}
              className="space-y-6 lg:order-1"
            >
              <h3 className="text-3xl md:text-4xl font-bold">Venda Rápido e Fácil</h3>
              <p className="text-lg text-muted-foreground">
                Selecione produtos do catálogo, escolha o cliente, defina as formas de pagamento. Tudo em uma tela.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Package, text: "Busca rápida de produtos" },
                  { icon: CreditCard, text: "Múltiplas formas de pagamento" },
                  { icon: Users, text: "Vincule clientes às vendas" },
                  { icon: CheckCircle, text: "Status: pendente, pago ou confirmado" }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-4 text-lg"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <span>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleIn}
            whileHover={{ scale: 1.02 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20"></div>
              <img 
                src="/print-tela-de-abertura-e-fechamento-de-caixas.png" 
                alt="Abertura e fechamento de caixa" 
                className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full"
              />
            </div>
            <div className="text-center mt-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Abertura e Fechamento de Caixa</h3>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Controle preciso de cada sessão de caixa. Abra com o valor inicial, feche com conferência de valores e breakdowns detalhados por conta bancária.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Products and Management Section */}
      <section id="gestao-negocio" className="py-32 bg-gradient-to-b from-accent/20 to-background relative overflow-hidden">
        <motion.div 
          className="absolute top-1/2 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{ x: [0, 50, 0], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Gestão Completa do Negócio
            </h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              Produtos, clientes, fornecedores - tudo organizado e acessível.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInFromLeft}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-produtos.png" 
                  alt="Cadastro de produtos" 
                  className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full"
                />
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInFromRight}
              className="space-y-6"
            >
              <h3 className="text-3xl md:text-4xl font-bold">Catálogo de Produtos Completo</h3>
              <p className="text-lg text-muted-foreground">
                Cadastre seus produtos com foto, preços de venda e custo, código SKU e mais. Organize por categoria e tenha tudo na palma da mão.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Package, text: "Cadastro com foto e detalhes completos" },
                  { icon: BarChart3, text: "Controle de estoque e preços" },
                  { icon: TrendingUp, text: "Análise de margem de lucro" }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-4 text-lg"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <span>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-clientes.png" 
                  alt="Gestão de clientes" 
                  className="relative rounded-2xl shadow-2xl border-4 border-secondary/20 w-full"
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Gestão de Clientes</h3>
              <p className="text-lg text-muted-foreground">
                Mantenha o cadastro completo dos seus clientes com telefone, email, WhatsApp e histórico de compras.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-fornecedores.png" 
                  alt="Gestão de fornecedores" 
                  className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full"
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Gestão de Fornecedores</h3>
              <p className="text-lg text-muted-foreground">
                Organize seus fornecedores com dados de contato e histórico de compras. Saiba para quem você pagou e quanto.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advanced Controls Section */}
      <section id="controles-avancados" className="py-32 bg-background relative">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Controles Avançados
            </h2>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              Recursos profissionais para <strong>levar seu negócio ao próximo nível</strong>.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-configuracao-de-contas.png" 
                  alt="Configuração de contas bancárias" 
                  className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full"
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Contas Bancárias</h3>
              <p className="text-lg text-muted-foreground">
                Configure múltiplas contas bancárias e formas de pagamento. Controle cada centavo com precisão.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-configuracao-de-categoria-de-despesas.png" 
                  alt="Categorias de despesas" 
                  className="relative rounded-2xl shadow-2xl border-4 border-secondary/20 w-full"
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Categorias de Despesas</h3>
              <p className="text-lg text-muted-foreground">
                Organize suas despesas por categoria: aluguel, energia, compra de mercadoria, salários e muito mais.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-usuarios.png" 
                  alt="Gestão de usuários" 
                  className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full"
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Controle de Usuários</h3>
              <p className="text-lg text-muted-foreground">
                Adicione vendedores, caixas e outros usuários. Defina permissões personalizadas por função.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={scaleIn}
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary rounded-2xl blur-2xl opacity-20"></div>
                <img 
                  src="/print-tela-de-integracao-com-chatwoot.png" 
                  alt="Integração com Chatwoot" 
                  className="relative rounded-2xl shadow-2xl border-4 border-secondary/20 w-full"
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Integração com Chatwoot</h3>
              <p className="text-lg text-muted-foreground">
                Integre com Chatwoot para atender seus clientes de forma profissional e centralizada.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleIn}
            whileHover={{ scale: 1.02 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-2xl blur-2xl opacity-20"></div>
              <img 
                src="/print-tela-de-relatorios.png" 
                alt="Relatórios avançados" 
                className="relative rounded-2xl shadow-2xl border-4 border-primary/20 w-full"
              />
            </div>
            <div className="text-center mt-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Relatórios Detalhados</h3>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Exporte relatórios em Excel, filtre por período, cliente, produto ou forma de pagamento. Análises profundas para tomada de decisão.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quem Usa */}
      <section id="quem-usa" className="py-20 bg-gradient-to-b from-accent/20 to-background">
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            Quem Usa o Caixa Fácil Já
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Store, title: "Lojas de Roupas", desc: "Registre vendas rapidamente, acompanhe seus clientes e veja quanto vendeu no dia, semana ou mês." },
              { icon: ShoppingCart, title: "Mercadinhos e Mercearias", desc: "Controle entradas e saídas do caixa, organize produtos e fornecedores de forma simples." },
              { icon: Gift, title: "Lojas de Presentes e Variedades", desc: "Cadastre produtos com foto, registre vendas e tenha visão clara do seu resultado financeiro." }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={scaleIn}
                whileHover={{ scale: 1.05 }}
              >
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardHeader>
                    <item.icon className="w-14 h-14 text-primary mb-4" />
                    <CardTitle className="text-2xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{item.desc}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Grid */}
      <section id="tudo-que-faz" className="py-20 bg-gradient-to-br from-secondary/10 via-primary/5 to-secondary/10">
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            Tudo Que Você Consegue Fazer
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Package, title: "Cadastrar Produtos", desc: "Adicione produtos com nome, preço, custo e foto. Organize seu catálogo." },
              { icon: ShoppingCart, title: "Registrar Vendas", desc: "Selecione produtos, cliente, formas de pagamento. Status: pendente, pago ou confirmado." },
              { icon: ArrowDownCircle, title: "Registrar Despesas", desc: "Anote saídas do caixa por categoria e fornecedor." },
              { icon: Users, title: "Organizar Clientes", desc: "Cadastre clientes com telefone, email e WhatsApp. Veja histórico de compras." },
              { icon: Building2, title: "Gerenciar Fornecedores", desc: "Mantenha fornecedores cadastrados. Saiba para quem pagou e quanto." },
              { icon: TrendingUp, title: "Ver Saldo e Relatórios", desc: "Dashboard visual com totais de entradas, saídas e saldo." },
              { icon: UserCog, title: "Controle de Usuários", desc: "Adicione vendedores e caixas. Defina permissões personalizadas." },
              { icon: Shield, title: "Perfis e Permissões", desc: "Controle quem pode ver, criar, editar ou excluir." },
              { icon: Settings, title: "Configurações Avançadas", desc: "Contas bancárias, categorias, formas de pagamento e mais." }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={scaleIn}
                whileHover={{ scale: 1.05, rotateY: 5 }}
              >
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardHeader>
                    <feature.icon className="w-10 h-10 text-primary mb-3" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{feature.desc}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="planos" className="py-32 bg-gradient-to-br from-primary via-primary/90 to-secondary text-white relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 opacity-10"
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
        </motion.div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Escolha o Plano Ideal para Você</h2>
            <p className="text-xl opacity-90">Planos flexíveis que crescem com seu negócio</p>
          </motion.div>
          
          {plansLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          ) : plans && plans.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {plans.map((plan: any, index: number) => {
                const isRecommended = plan.id === 'plus_whats';
                const planFeatures = (plan.features as string[]) || [];
                
                return (
                  <motion.div
                    key={plan.id}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={scaleIn}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`bg-white text-slate-900 shadow-2xl h-full flex flex-col ${isRecommended ? 'border-primary border-2 relative' : ''}`}>
                      {isRecommended && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground text-sm px-4 py-1">
                            Mais Popular
                          </Badge>
                        </div>
                      )}
                      <CardHeader className="text-center">
                        <CardTitle className="text-2xl md:text-3xl mb-2 flex items-center justify-center gap-2">
                          {plan.id === 'plus' && <Package className="h-6 w-6 text-primary" />}
                          {plan.id === 'plus_whats' && <MessageSquare className="h-6 w-6 text-primary" />}
                          {plan.id === 'plus_whats_ia' && <Sparkles className="h-6 w-6 text-primary" />}
                          {plan.name}
                        </CardTitle>
                        <div className="text-5xl md:text-6xl font-bold text-primary my-4">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(plan.price)}<span className="text-xl">/mês</span>
                        </div>
                        <CardDescription className="text-base">{plan.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col">
                        <ul className="space-y-3 mb-6 flex-1">
                          {planFeatures.map((feature: string, i: number) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        {plan.includes_chatwoot && (
                          <div className="bg-blue-50 p-4 rounded-lg mb-4">
                            <p className="text-sm text-slate-700">
                              <strong>Chatwoot:</strong> Plataforma omnichannel que centraliza todas as conversas em um único lugar, permitindo melhor gestão do atendimento ao cliente e aumento da satisfação.
                            </p>
                          </div>
                        )}
                        {plan.includes_ai && (
                          <div className="bg-purple-50 p-4 rounded-lg mb-4">
                            <p className="text-sm text-slate-700">
                              <strong>IA para Vendas e Atendimento:</strong> Automatiza e otimiza o atendimento ao cliente, oferecendo respostas rápidas e precisas, melhorando a experiência do usuário e aumentando as taxas de conversão.
                            </p>
                          </div>
                        )}
                        <Button
                          size="lg"
                          className="w-full text-lg py-6"
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                          Começar Agora
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl opacity-90">Nenhum plano disponível no momento</p>
            </div>
          )}

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 inline-block">
              <p className="text-2xl font-semibold mb-2">🎁 Teste Grátis por 14 Dias</p>
              <p className="text-lg opacity-90">Sem cartão necessário. Cancele quando quiser.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="depoimentos" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            O Que Nossos Clientes Dizem
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Maria Silva",
                business: "Loja Bella Moda",
                text: "Antes eu anotava tudo em caderno. Agora vejo rapidamente se o dia foi bom ou ruim. Simples e prático!",
                initials: "MS"
              },
              {
                name: "João Santos",
                business: "Mercadinho São José",
                text: "Registro vendas e despesas em segundos. O saldo consolidado me dá controle que eu nunca tive antes.",
                initials: "JS"
              },
              {
                name: "Ana Costa",
                business: "Presentes & Cia",
                text: "Cadastrei meus produtos com foto, agora é só selecionar na venda. Economizo muito tempo!",
                initials: "AC"
              }
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.2 }}
                whileHover={{ scale: 1.05, y: -10 }}
              >
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                        {testimonial.initials}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                        <CardDescription>{testimonial.business}</CardDescription>
                      </div>
                    </div>
                    <div className="text-yellow-500 text-xl">⭐⭐⭐⭐⭐</div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground italic text-base">"{testimonial.text}"</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="py-32 bg-gradient-to-br from-secondary via-primary/90 to-primary text-white relative overflow-hidden">
        <motion.div 
          className="absolute inset-0 opacity-20"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity }}
        >
          <div className="absolute top-20 left-20 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
        </motion.div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Pronto para Ter Controle Total do Seu Caixa?
            </h2>
            <p className="text-2xl md:text-3xl mb-12 opacity-90 max-w-3xl mx-auto">
              Comece hoje mesmo. Teste grátis por 14 dias. Sem cartão necessário. Cancele quando quiser.
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-2xl px-12 py-8"
            >
              Começar Agora - Grátis por 14 Dias
              <ArrowRight className="ml-2 w-8 h-8" />
            </Button>
          </motion.div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
